// The model's hands on the 3D view. Every action returns an `effect` the site applies; the
// tool itself only resolves ids, colours and numbers so the effect is self-contained.
import type { PropsElement } from '../props';
import type { Bounds, SceneEffect, ToolFn, ToolSpec } from '../types';
import { fieldValue } from './fields';
import { centre, radius, union } from './geometry';
import { inStorey, ofType, round } from './select';
import { materialMatcher } from './vocabulary';

export const sceneSpec: ToolSpec = {
  name: 'scene',
  description:
    'Change the 3D view. action: focus (fly the camera to elements), isolate (show only these elements, everything else fades), section (cut the building horizontally at a storey or a height in metres so the visitor looks down into it), colour_by (colour every element by a field: material, type, storey, or a numeric field like volume as a gradient), label (float a text label on elements, e.g. their volume), reset (undo all of the above). Select elements with ids, or with type/storey/material filters like query_elements. Call this after you know which elements the answer is about.',
  input_schema: {
    type: 'object',
    properties: {
      action: {
        type: 'string',
        enum: ['focus', 'isolate', 'section', 'colour_by', 'label', 'reset'],
      },
      ids: { type: 'array', items: { type: 'integer' } },
      type: { type: 'string' },
      storey: { type: 'string' },
      material: { type: 'string' },
      field: {
        type: 'string',
        description:
          'colour_by / label: material, type, storey, volume, area, q:<Quantity>, p:<Pset>.<Prop>',
      },
      at: { type: 'number', description: 'section: height in metres above the ground floor' },
    },
    required: ['action'],
  },
};

const PALETTE = [
  '#4cc9f0',
  '#f0b429',
  '#7bd88f',
  '#e0453a',
  '#b892ff',
  '#ff8fab',
  '#57b8ff',
  '#f4a261',
  '#8ecae6',
  '#c9f04c',
  '#ffd6a5',
  '#a0c4ff',
];
const GRADIENT = ['#1b3a5c', '#2d6a9f', '#4cc9f0', '#a6f0d0', '#f0e68c', '#f0b429', '#e0453a'];

export const scene =
  (elements: PropsElement[], bounds?: Record<number, Bounds>): ToolFn =>
  (input) => {
    const action = String(input.action ?? '');
    if (action === 'reset') return { ok: true, effect: { kind: 'reset' } satisfies SceneEffect };
    const picked = select(elements, input);
    const ids = picked.map((e) => e.expressID);
    switch (action) {
      case 'focus': {
        const boxes = ids.map((id) => bounds?.[id]).filter((b): b is Bounds => !!b);
        const box = union(boxes);
        if (!box)
          return { error: ids.length ? 'no geometry for these elements' : 'nothing selected' };
        const effect: SceneEffect = {
          kind: 'focus',
          ids,
          center: centre(box),
          radius: round(radius(box), 2),
        };
        return { count: ids.length, ids, effect };
      }
      case 'isolate': {
        if (!ids.length) return { error: 'nothing selected' };
        return { count: ids.length, ids, effect: { kind: 'isolate', ids } satisfies SceneEffect };
      }
      case 'section': {
        let y: number | null = input.at != null && input.at !== '' ? Number(input.at) : null;
        let label = y != null ? `${y} m` : '';
        if (y == null && input.storey) {
          const boxes = inStorey(elements, input.storey)
            .map((e) => bounds?.[e.expressID])
            .filter((b): b is Bounds => !!b);
          const box = union(boxes);
          if (!box) return { error: `no geometry for storey ${input.storey}` };
          // cut just under the ceiling so the rooms of that storey read as a plan
          y = round(box[1] + (box[4] - box[1]) * 0.85, 2);
          label = String(input.storey);
        }
        if (y == null || !Number.isFinite(y))
          return { error: 'section needs a storey or a height' };
        return { y, label, effect: { kind: 'section', y, label } satisfies SceneEffect };
      }
      case 'colour_by': {
        const field = String(input.field ?? 'material');
        // rooms are translucent boxes; painting them hides the building unless asked for
        const pool = picked.length
          ? picked
          : elements.filter((e) => !e.type.toUpperCase().startsWith('IFCSPACE'));
        const all = pool.map((e) => ({ id: e.expressID, v: fieldValue(e, field) }));
        const values = all.filter((x) => x.v != null && x.v !== '');
        const unassigned = all.length - values.length;
        const numeric = values.filter((x) => typeof x.v === 'number');
        if (numeric.length && numeric.length >= values.length * 0.5) {
          const nums = numeric.map((x) => x.v as number);
          const lo = Math.min(...nums);
          const hi = Math.max(...nums);
          const bins = GRADIENT.length;
          const groups = GRADIENT.map((color, i) => ({
            key: `${round(lo + ((hi - lo) * i) / bins, 1)}–${round(lo + ((hi - lo) * (i + 1)) / bins, 1)}`,
            color,
            ids: [] as number[],
          }));
          for (const x of numeric) {
            const t = hi > lo ? ((x.v as number) - lo) / (hi - lo) : 0;
            groups[Math.min(bins - 1, Math.floor(t * bins))]!.ids.push(x.id);
          }
          const effect: SceneEffect = {
            kind: 'colour',
            field,
            groups: groups.filter((g) => g.ids.length),
          };
          return {
            field,
            min: round(lo, 2),
            max: round(hi, 2),
            groups: effect.groups.map((g) => ({ range: g.key, count: g.ids.length })),
            effect,
          };
        }
        const byKey = new Map<string, number[]>();
        for (const x of values) {
          const k = String(x.v);
          byKey.set(k, [...(byKey.get(k) ?? []), x.id]);
        }
        const sorted = [...byKey.entries()].sort((a, b) => b[1].length - a[1].length);
        const groups = sorted
          .slice(0, PALETTE.length)
          .map(([key, ids], i) => ({ key, color: PALETTE[i]!, ids }));
        const rest = sorted.slice(PALETTE.length).flatMap(([, ids]) => ids);
        if (rest.length) groups.push({ key: 'other', color: '#3a3f4a', ids: rest });
        const effect: SceneEffect = { kind: 'colour', field, groups };
        return {
          field,
          unassigned,
          groups: groups.map((g) => ({ key: g.key, count: g.ids.length })),
          effect,
        };
      }
      case 'label': {
        if (!ids.length) return { error: 'nothing selected' };
        const field = input.field ? String(input.field) : null;
        const items = picked.slice(0, 40).map((e) => {
          const v = field ? fieldValue(e, field) : null;
          const text = field
            ? typeof v === 'number'
              ? `${round(v, 2)}${unitOf(field)}`
              : String(v ?? '–')
            : e.name.slice(0, 40);
          return { id: e.expressID, text };
        });
        return { count: items.length, ids, effect: { kind: 'label', items } satisfies SceneEffect };
      }
      default:
        return { error: `unknown action ${action}` };
    }
  };

function select(elements: PropsElement[], input: Record<string, unknown>): PropsElement[] {
  if (Array.isArray(input.ids) && input.ids.length) {
    const want = new Set((input.ids as unknown[]).map(Number));
    return elements.filter((e) => want.has(e.expressID));
  }
  if (!input.type && !input.storey && !input.material) return [];
  let hits = inStorey(ofType(elements, input.type), input.storey);
  const m = materialMatcher(input.material);
  if (m) hits = hits.filter((e) => e.material?.names.some(m.test));
  return hits;
}

const unitOf = (field: string) =>
  field === 'volume' || /volume/i.test(field)
    ? ' m³'
    : field === 'area' || /area/i.test(field)
      ? ' m²'
      : '';
